<template>
  <div
    class="radio-group"
    role="radiogroup"
    :aria-label="ariaLabel"
  >
    <div
      v-for="(option, index) in options"
      :key="index"
      class="radio-group__element"
    >
      <input
        :id="`${option.value}-${option.id}`"
        type="radio"
        :name="name"
        :value="option.value"
        :checked="option.value === selectedValue"
        class="radio-group__input"
        @change="selectOption(option.value)"
        v-text="option.label"
      >
      <label
        :for="`${option.value}-${option.id}`"
        class="radio-group__label"
        v-text="option.label"
      />
    </div>
  </div>
</template>

<script>

/**
 * Component: Radio Group
 * -----------------------------------------------------------------------------
 * - Custom radio group component.
 *
 * @param {Array}  [options] - Array of formatted options objects, each should contain `id`, `label` and `value`.
 * @param {String} [predefinedValue] - The value to be pre-selected (first option by default).
 * @param {String} [name] - Input name for usage with form data.
 * @param {String} [ariaLabel] - Custom aria label to describe the radio group.
 */

export default {
  name: 'RadioGroup',

  props: {
    options: {
      type: Array,
      default: () => [],
    },
    predefinedValue: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
  },

  emits: ['on-option-selected'],

  data() {
    return {
      selectedValue: this.predefinedValue || this.options[0]?.value,
    }
  },

  computed: {

    /**
     * Calculates chosen option based on selected value.
     * @returns {object}
     */
    selectedOption() {
      return this.findOptionByValue(this.selectedValue) || this.options[0]
    },
  },

  created() {

    /**
     * Emit the default selected option to the parent component.
     */
    this.$emit('on-option-selected', this.selectedValue)
  },

  methods: {

    /**
     * Selects an option.
     *
     * @param {Object} value - The option value to be selected.
     * @emits `on-option-selected`
     */
    selectOption(value) {
      this.selectedValue = value

      this.$emit('on-option-selected', value)
    },

    /**
     * Finds an option in the options array by its value.
     *
     * @param {String} value - The value to search for.
     * @returns {object|boolean} The found option or `false` if not found.
     */
    findOptionByValue(value) {
      return this.options.find((option) => option.value === value) || false
    },
  },
}
</script>

<style src="./radio-group.scss" lang="scss"></style>
